// src/components/proposals/proposalNumber.js
//
// The rules for P numbers, pure and React-free so they can be unit-tested
// without pulling MSAL in behind a component — the same reason
// proposalStatus.js and opportunityPipeline.js exist as bare modules.
//
// The COUNTER is server-side and atomic (see the backend's sequenceService):
// deriving a number from the records themselves lets two proposals created in
// quick succession both take the same one. What lives here is only the
// backfill, which is a one-off pass over records that already exist and are
// not being created concurrently.
import { docTypeOf, DOC_LEAD } from "./docTypes";

export const FIRST_NUMBER = 1500;

/** P1500. The one place the prefix is written. */
export function formatProposalNumber(n) {
  return n == null || n === "" ? "" : `P${n}`;
}

/* ⚠ LEADS TAKE NO NUMBER. Craig's decision: a Lead is one click from a
   template and is the same document every time, so it does not consume an
   identity in the proposal sequence.

   ⚠ AND `docTypeOf` IS THE ONLY WAY TO ASK. Every record written before Leads
   shipped has NO docType field at all, so `p.docType === "proposal"` is false
   for all of them — testing the raw field would skip the very records the
   backfill exists to number. Same trap as opportunity.status. */
export function takesNumber(p) {
  return !!p && docTypeOf(p) !== DOC_LEAD;
}

/** Does this record already carry a number? */
export function hasNumber(p) {
  return !!p && p.proposalNumber != null && p.proposalNumber !== "";
}

/**
 * Assign numbers to every proposal that has none, in creation order.
 *
 * IDEMPOTENT, and that is not incidental — this runs on load, so it runs many
 * times. A record that already has a number keeps it, always.
 *
 * ⚠ NEVER REUSES. Numbering resumes after the highest already in use rather
 * than filling gaps: a gap means a proposal was deleted or a reservation was
 * never saved, and reissuing that number would put an identity a customer may
 * already have seen onto a different document.
 *
 * Returns the new array, how many were assigned, and the first free number —
 * which the caller hands to the server counter so new proposals carry on from
 * there rather than colliding with the backfill.
 */
export function backfillProposalNumbers(proposals = []) {
  const list = Array.isArray(proposals) ? proposals : [];

  const used = list.filter(hasNumber).map((p) => Number(p.proposalNumber))
    .filter((n) => Number.isFinite(n));
  let next = used.length ? Math.max(...used) + 1 : FIRST_NUMBER;

  /* Creation order, so the numbers read chronologically. Verified against the
     real data: every record carries a distinct, parseable createdAt, so no
     tie-break rule is needed — but a missing one must not sort to the front and
     silently claim P1500, hence the fallback to Infinity. */
  const order = [...list].sort((a, b) =>
    (Number(a.createdAt) || Infinity) - (Number(b.createdAt) || Infinity));

  const assign = new Map();
  for (const p of order) {
    if (!takesNumber(p) || hasNumber(p)) continue;
    assign.set(p.id, next++);
  }

  if (!assign.size) return { proposals: list, assigned: 0, nextFree: next };

  return {
    // Mapped in the ORIGINAL order — the sort above decides which number each
    // record gets, never the order they are stored in.
    proposals: list.map((p) => (assign.has(p.id) ? { ...p, proposalNumber: assign.get(p.id) } : p)),
    assigned: assign.size,
    nextFree: next,
  };
}

/** Match a proposal against a typed search term — "1500", "P1500", "p1500". */
export function matchesNumberSearch(p, term) {
  const q = String(term || "").trim().toLowerCase().replace(/^p/, "");
  if (!q) return true;
  if (!hasNumber(p)) return false;
  return String(p.proposalNumber).includes(q);
}
